/* ============================================================================
   M2 — Partner agreement pane (route: /supply/agreements?p=<partnerId>)
   Live data: GET /api/courierPartner/parent/agreement/timeline/{parentId}.
   Renders:
     header card = avatar + name + vertical chips + legal + Open Partner 360
     KPI strip   = Total Agreements / Active / Pending Signature (partner scope)
     timeline    = stage-aware vertical timeline grouped by effective-from year
                   with per-entry Download / Update Stage (M2.5) / Approve / Reject
                   / Mark Superseded actions and preview link.
   The stage is promoted IN PLACE on the server — an agreement's email-received →
   signed history lives in its documents[]/events[], not as a second entry.
   ========================================================================== */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useNavigate} from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import HistoryIcon from '@mui/icons-material/History'
import SyncIcon from '@mui/icons-material/Sync'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined'

import {
  Badge,
  KpiStrip,
  ConfirmDialog,
  fmtDate,
  fmtDateTime,
  initials,
} from '../components'
import {apiError} from '../supplyMeta'
import {
  useAgreementTimelineQuery,
  useLazyDownloadAgreementQuery,
  useApproveAgreementMutation,
  useRejectAgreementMutation,
  useSupersedeAgreementMutation,
} from '../../../redux/supplyApiSlice'
import {
  agrTypeFull,
  isOpenStage1,
  isSignedGoverning,
  isPendingApproval,
  riskChips,
  colorFor,
} from './agreementHealth'
import StageDrawer from './StageDrawer'

/* chips for a partner's verticals */
function VertChips({verticals}) {
  return (
    <>
      {(verticals || []).map((v) => (
        <span key={v} className={'chip chip-' + String(v).toLowerCase()}>
          {v}
        </span>
      ))}
    </>
  )
}

/* chips for an agreement's verticals (server: list, e.g. ["D2C","PTL"]) */
function AgrVertChips({verticals}) {
  return (
    <>
      {(verticals || []).map((v) => (
        <span key={v} className={'chip chip-' + String(v).toLowerCase()}>
          {v}
        </span>
      ))}
    </>
  )
}

/* code chips (max 3 + overflow) — courier account codes */
function CodeChips({codes}) {
  codes = codes || []
  if (!codes.length) return <span className='muted'>—</span>
  const max = 3
  const shown = codes.slice(0, max)
  const rest = codes.slice(max)
  return (
    <span style={{display: 'inline-flex', flexWrap: 'wrap', gap: 5, verticalAlign: 'top'}}>
      {shown.map((c) => (
        <span key={c} className='tag tag-mono'>
          {c}
        </span>
      ))}
      {rest.length ? (
        <span className='tag tip' title={rest.join(', ')}>
          +{rest.length} more
        </span>
      ) : null}
    </span>
  )
}

/* ════════════════════ MAIN PANE ════════════════════ */
export default function PartnerTimeline({
  partnerId,
  partner,
  isAdmin,
  canApprove,
  template,
  version,
  onUpload,
  onPreview,
  onChanged,
  toast,
}) {
  const navigate = useNavigate()
  const [stageAgr, setStageAgr] = useState(null) // agreement being moved Stage 1 → 2
  const [supAgr, setSupAgr] = useState(null) // agreement being marked superseded
  const [rejAgr, setRejAgr] = useState(null) // agreement being rejected (maker-checker)
  const [busyId, setBusyId] = useState('') // agreement id with an action in flight
  const [fetchDownload] = useLazyDownloadAgreementQuery()
  const [approveAgreement] = useApproveAgreementMutation()
  const [rejectAgreement] = useRejectAgreementMutation()
  const [supersedeAgreement] = useSupersedeAgreementMutation()

  /* SUBSCRIBED timeline read — the component re-renders automatically whenever an
     agreement mutation invalidates the Agreement cache, so Approve / Update Stage
     reflect the new state instantly without a manual page refresh. (The previous
     lazy `.unwrap()` into local state could race with the invalidation refetch and
     resolve to stale data, which is why a hard refresh was needed.) */
  const {
    data: items = [],
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useAgreementTimelineQuery(partnerId, {skip: !partnerId})
  const loadErr = isError ? apiError(error) : ''

  /* the overview bumps `version` after an upload — refetch to pick that up. */
  useEffect(() => {
    if (version) refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version])

  const agrs = items.map((it) => it.agreement)
  const total = agrs.length
  const active = agrs.filter(isSignedGoverning).length
  const pendingSig = agrs.filter(isOpenStage1).length

  const p = partner || (agrs.length ? {name: agrs[0].parentName, verticals: agrs[0].verticals} : null)
  const pName = p ? p.name : 'partner'

  /* ── actions (server-backed) ── */
  const download = async (a) => {
    setBusyId(a.id)
    try {
      const r = await fetchDownload({id: a.id}).unwrap()
      if (r && r.url) window.open(r.url, '_blank', 'noopener')
      else toast('No document available for download.', 'error')
    } catch (e) {
      toast(apiError(e), 'error')
    }
    setBusyId('')
  }
  const approve = async (a) => {
    setBusyId(a.id)
    try {
      const res = await approveAgreement(a.id).unwrap()
      const n = (res && res.supersededIds ? res.supersededIds : []).length
      toast('Agreement approved — now Active & governing' + (n ? ' — superseded ' + n + ' prior.' : '.'), 'success')
      onChanged()
    } catch (e) {
      toast(apiError(e), 'error')
    }
    setBusyId('')
  }
  const confirmReject = async (reason) => {
    const a = rejAgr
    setRejAgr(null)
    if (!a) return
    try {
      await rejectAgreement({id: a.id, reason}).unwrap()
      toast('Agreement rejected. The maker can re-upload a corrected version.', 'success')
      onChanged()
    } catch (e) {
      toast(apiError(e), 'error')
    }
  }
  const confirmSupersede = async (reason) => {
    const a = supAgr
    setSupAgr(null)
    if (!a) return
    try {
      await supersedeAgreement({id: a.id, reason}).unwrap()
      toast('Agreement marked superseded. Audit recorded. Code badges updated.', 'success')
      onChanged()
    } catch (e) {
      toast(apiError(e), 'error')
    }
  }

  /* timeline entries newest first (server already sorts by effectiveFrom desc) */
  let lastYear = null

  return (
    <>
      {/* ── partner header card ── */}
      <div className='card card-pad' style={{marginBottom: 16}}>
        <div className='row between center wrap gap16'>
          <div className='row center gap12'>
            <span className='avatar avatar-lg' style={{background: colorFor(pName)}}>
              {initials(pName)}
            </span>
            <div>
              <div className='row center gap8' style={{flexWrap: 'wrap'}}>
                <h2 style={{margin: 0, fontSize: 20, fontWeight: 800}}>{pName}</h2>
                <VertChips verticals={p ? p.verticals : []} />
              </div>
              {p && p.legalEntityName ? (
                <div className='muted' style={{fontSize: 12.5, marginTop: 3}}>
                  {p.legalEntityName}
                </div>
              ) : null}
            </div>
          </div>
          <button className='btn btn-sm' onClick={() => navigate('/supply/partners/' + partnerId)}>
            <VisibilityOutlinedIcon style={{width: 15, height: 15}} />
            Open Partner 360
          </button>
        </div>
      </div>

      {/* ── partner-scoped KPIs ── */}
      <KpiStrip
        cards={[
          {
            label: 'Total Agreements',
            value: loading ? '…' : total,
            icon: <DescriptionOutlinedIcon />,
            sub: 'all stages & types',
          },
          {
            label: 'Active',
            value: loading ? '…' : active,
            icon: <CheckCircleOutlineIcon />,
            sub: 'signed & governing',
          },
          {
            label: 'Pending Signature',
            value: loading ? '…' : pendingSig,
            icon: <AccessTimeOutlinedIcon />,
            sub: 'Stage 1: email received',
          },
        ]}
      />

      {/* ── timeline (loading / error / empty / list) ── */}
      {loading ? (
        <div className='card card-pad'>
          <div className='muted' style={{display: 'flex', alignItems: 'center', gap: 8, fontSize: 13}}>
            <SyncIcon className='ps-spin' style={{width: 16, height: 16}} />
            Loading agreement timeline…
          </div>
        </div>
      ) : loadErr ? (
        <div className='card card-pad'>
          <div className='warnbox err'>
            <WarningAmberIcon style={{width: 16, height: 16}} />
            <div>
              Could not load the agreement timeline: {loadErr}{' '}
              <button className='lnk' style={{border: 'none', background: 'none', cursor: 'pointer', font: 'inherit'}} onClick={onChanged}>
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : !items.length ? (
        <div className='card card-pad'>
          <div className='empty'>
            <div className='ic'>
              <DescriptionOutlinedIcon style={{width: 30, height: 30}} />
            </div>
            <h3>No agreements on record</h3>
            <p>No agreements for {pName} in the vertical(s) you can view.</p>
            {isAdmin ? (
              <button
                className='btn btn-sm btn-primary'
                style={{marginTop: 10}}
                onClick={() => onUpload(partnerId, [])}
              >
                <AddIcon style={{width: 14, height: 14}} />
                Upload Agreement
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className='card card-pad'>
          <div className='row between center wrap gap12' style={{marginBottom: 16}}>
            <h3 style={{margin: 0, fontSize: 15}}>Agreement Timeline — {pName}</h3>
            {isAdmin ? (
              <button className='btn btn-sm btn-primary' onClick={() => onUpload(partnerId, [])}>
                <AddIcon style={{width: 14, height: 14}} />
                Upload Agreement
              </button>
            ) : null}
          </div>
          <div className='timeline'>
            {items.map((it) => {
              const a = it.agreement
              const yr = new Date(a.effectiveFrom).getFullYear()
              const marker = yr !== lastYear
              if (marker) lastYear = yr
              return (
                <React.Fragment key={a.id}>
                  {marker ? (
                    <div className='seclabel' style={{margin: '4px 0 10px', color: 'var(--ink-2)'}}>
                      {yr}
                    </div>
                  ) : null}
                  <TimelineEntry
                    item={it}
                    isAdmin={isAdmin}
                    canApprove={canApprove}
                    busy={busyId === a.id}
                    onPreview={onPreview}
                    onDownload={download}
                    onStage={setStageAgr}
                    onSupersede={setSupAgr}
                    onApprove={approve}
                    onReject={setRejAgr}
                  />
                </React.Fragment>
              )
            })}
          </div>
        </div>
      )}

      {/* ── M2.5 — Stage 1 → Stage 2 update drawer ── */}
      <StageDrawer
        open={!!stageAgr}
        agr={stageAgr}
        template={template}
        toast={toast}
        onClose={() => setStageAgr(null)}
        onDone={() => {
          setStageAgr(null)
          onChanged()
        }}
      />

      {/* ── Mark Superseded confirm (reason mandatory, history never deleted) ── */}
      <ConfirmDialog
        open={!!supAgr}
        onClose={() => setSupAgr(null)}
        title={supAgr ? 'Mark "' + supAgr.title + '" as superseded?' : ''}
        danger
        needReason
        okLabel='Mark Superseded'
        body='This agreement will be retained in the timeline for audit but will no longer govern any courier code.'
        bullets={[
          'The entry stays visible (history is never deleted)',
          'Linked code agreement badges recompute immediately',
          'A newer signed agreement should govern these codes',
        ]}
        onOk={confirmSupersede}
      />

      {/* ── Reject confirm (maker-checker, reason mandatory) ── */}
      <ConfirmDialog
        open={!!rejAgr}
        onClose={() => setRejAgr(null)}
        title={rejAgr ? 'Reject "' + rejAgr.title + '"?' : ''}
        danger
        needReason
        okLabel='Reject'
        body='The agreement will be marked Rejected (terminal) and will never govern. The maker can upload a corrected version.'
        onOk={confirmReject}
      />
    </>
  )
}

/* ════════════════════ TIMELINE ENTRY ════════════════════ */
function TimelineEntry({item, isAdmin, canApprove, busy, onPreview, onDownload, onStage, onSupersede, onApprove, onReject}) {
  const a = item.agreement
  let cls = a.stage === 'SIGNED' ? 'green' : 'amber'
  if (a.status === 'SUPERSEDED' || a.status === 'REJECTED') cls = 'red'
  const openS1 = isOpenStage1(a)
  const pendingAppr = isPendingApproval(a)
  const chips = riskChips(item, fmtDate)
  const lastDoc = (a.documents || [])[(a.documents || []).length - 1]

  return (
    <div className={'tl-item ' + cls}>
      <div className='tl-card'>
        <div className='row between center wrap gap8'>
          <div className='row center gap8' style={{flexWrap: 'wrap'}}>
            {a.stage === 'SIGNED' ? (
              <Badge status='signed' label='Stage 2: Signed' />
            ) : (
              <Badge status='draft' label='Stage 1: Email Received' />
            )}
            {a.status === 'SUPERSEDED' ? <Badge status='expired' label='Superseded' /> : null}
            {a.status === 'REJECTED' ? <Badge status='rejected' label='Rejected' /> : null}
            {pendingAppr ? <Badge status='approvalpending' label='Pending approval' /> : null}
            <b style={{fontSize: 13}}>{agrTypeFull(a)}</b>
            <AgrVertChips verticals={a.verticals} />
          </div>
          <span className='muted' style={{fontSize: 12}}>
            Effective {fmtDate(a.effectiveFrom)}
            {a.effectiveTo ? ' → ' + fmtDate(a.effectiveTo) : ' · open-ended'}
          </span>
        </div>

        {/* title — opens the document preview */}
        <div style={{fontSize: 13.5, marginTop: 8, fontWeight: 500}}>
          <span
            className='lnk'
            role='button'
            tabIndex={0}
            style={{cursor: 'pointer'}}
            onClick={() => onPreview(a)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onPreview(a)
              }
            }}
          >
            {a.title}
          </span>
        </div>
        {chips.length ? (
          <span style={{display: 'inline-flex', flexWrap: 'wrap', gap: 4, marginTop: 6}}>
            {chips.map((c, i) =>
              c.blue ? (
                <span key={i} className='mini-pill' style={{fontSize: 10.5, background: 'rgba(25,118,210,.12)', color: '#1976D2'}}>
                  {c.text}
                </span>
              ) : (
                <span key={i} className={'mini-pill ' + c.cls} style={{fontSize: 10.5}}>
                  {c.text}
                </span>
              )
            )}
          </span>
        ) : null}

        <div className='tl-meta'>
          Applies to:{' '}
          {a.appliesToAll ? (
            <span className='tag'>All {a.parentName} codes</span>
          ) : (
            <CodeChips codes={a.linkedCpAccountCodes} />
          )}
        </div>
        {lastDoc ? (
          <div className='tl-meta'>
            Uploaded by {lastDoc.uploadedBy} on {fmtDateTime(lastDoc.uploadedAt)}
            {openS1 ? (
              <>
                {' · '}
                <span style={{color: 'var(--warning)', fontWeight: 600}}>Signing pending</span>
              </>
            ) : null}
            {a.signedAt ? (
              <>
                {' · '}
                <span style={{color: 'var(--success)', fontWeight: 600}}>
                  Signed by {a.signedBy} on {fmtDateTime(a.signedAt)}
                </span>
              </>
            ) : null}
          </div>
        ) : null}
        {a.notes ? (
          <div className='tl-meta' style={{fontStyle: 'italic'}}>
            Note: {a.notes}
          </div>
        ) : null}
        {a.status === 'SUPERSEDED' && a.supersededAt ? (
          <div className='tl-meta' style={{color: 'var(--error)'}}>
            Superseded on {fmtDateTime(a.supersededAt)}
            {a.supersedeReason ? ' — ' + a.supersedeReason : ''}
          </div>
        ) : null}
        {a.status === 'REJECTED' ? (
          <div className='tl-meta' style={{color: 'var(--error)'}}>
            Rejected{a.rejectedBy ? ' by ' + a.rejectedBy : ''}
            {a.rejectedAt ? ' on ' + fmtDateTime(a.rejectedAt) : ''}
            {a.rejectReason ? ' — ' + a.rejectReason : ''}
          </div>
        ) : null}

        <div style={{marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap'}}>
          <button className='btn btn-sm' disabled={busy} onClick={() => onDownload(a)}>
            <DownloadOutlinedIcon style={{width: 14, height: 14}} />
            Download
          </button>
          {isAdmin && openS1 ? (
            <button className='btn btn-sm btn-primary' disabled={busy} onClick={() => onStage(a)}>
              <CheckIcon style={{width: 14, height: 14}} />
              Update Stage
            </button>
          ) : null}
          {pendingAppr && canApprove ? (
            <>
              <button className='btn btn-sm btn-primary' disabled={busy} onClick={() => onApprove(a)}>
                <VerifiedUserOutlinedIcon style={{width: 14, height: 14}} />
                Approve
              </button>
              <button className='btn btn-sm' disabled={busy} onClick={() => onReject(a)}>
                <CloseIcon style={{width: 14, height: 14}} />
                Reject
              </button>
            </>
          ) : null}
          {isAdmin && a.status === 'ACTIVE' ? (
            <button className='btn btn-sm' disabled={busy} onClick={() => onSupersede(a)}>
              <HistoryIcon style={{width: 14, height: 14}} />
              Mark Superseded
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/* ════════════════════ PropTypes ════════════════════ */
VertChips.propTypes = {
  verticals: PropTypes.array,
}
AgrVertChips.propTypes = {
  verticals: PropTypes.array,
}
CodeChips.propTypes = {
  codes: PropTypes.array,
}
PartnerTimeline.propTypes = {
  partnerId: PropTypes.string.isRequired,
  partner: PropTypes.object,
  isAdmin: PropTypes.bool,
  canApprove: PropTypes.bool,
  template: PropTypes.string,
  version: PropTypes.number,
  onUpload: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  toast: PropTypes.func.isRequired,
}
TimelineEntry.propTypes = {
  item: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  canApprove: PropTypes.bool,
  busy: PropTypes.bool,
  onPreview: PropTypes.func,
  onDownload: PropTypes.func,
  onStage: PropTypes.func,
  onSupersede: PropTypes.func,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
}
